// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionUserEmail, getCookie, setCookie } from '../config/common';
import { hasRole } from '../utils/roles';
import { selectableAccounts } from '../utils/accounts';

function initialsFromName(name) {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Renders the signed-in user identity in the dashboard topnav.
 *
 * Modes:
 *   - default: name (+ seniority pill) and job title to the LEFT of the avatar.
 *   - compact: legacy mobile topbar variant — name hidden on tiny screens.
 *   - compactAvatar: avatar-only trigger that opens a click popover with the
 *     full identity card. Used in the desktop topnav so a narrow viewport
 *     never crowds out the page action buttons.
 */
export default function DashboardUserProfile({
  fullName = '',
  seniority = '',
  jobTitle = '',
  company = '',
  accountCtx = null,
  roles = [],
  compact,
  compactAvatar,
  onLogout,
  logoutBusy,
}) {
  // Same tested helper the left-nav picker uses (utils/accounts), so the two views of "which
  // accounts do I have" can never disagree — and a single-account user is filtered out here too.
  const linkedAccounts = selectableAccounts(accountCtx);

  const navigate = useNavigate();
  const initials = useMemo(() => initialsFromName(fullName), [fullName]);
  const email =
    typeof getSessionUserEmail === 'function' ? getSessionUserEmail() || '' : '';
  const displayName = (fullName && fullName.trim()) || email || 'Signed in';
  const [popoverOpen, setPopoverOpen] = useState(false);
  const wrapRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(() => getCookie('theme') === 'dark');

  useEffect(() => {
    const handler = (e) => setIsDarkMode(e.detail === 'dark');
    window.addEventListener('themechange', handler);
    return () => window.removeEventListener('themechange', handler);
  }, []);

  const toggleDarkMode = () => {
    const next = isDarkMode ? 'light' : 'dark';
    document.documentElement.setAttribute('data-bs-theme', next);
    setCookie('theme', next, 365);
    setIsDarkMode(!isDarkMode);
    window.dispatchEvent(new CustomEvent('themechange', { detail: next }));
  };

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [popoverOpen]);

  const textBlock = (
    <div className="dashboard-user-profile__text text-end">
      <div className="d-flex align-items-center gap-2 justify-content-end flex-wrap">
        <span
          className={`fw-semibold text-truncate dashboard-user-profile__name ${compact ? 'small' : ''}`}
          title={displayName}
        >
          {displayName}
        </span>
        {seniority ? (
          <span className="badge rounded-pill bg-primary flex-shrink-0" style={{ fontSize: '11px' }}>
            {seniority}
          </span>
        ) : null}
      </div>
      {jobTitle ? (
        <div
          className="text-muted text-truncate mt-1 dashboard-user-profile__job"
          style={{ fontSize: compact ? '0.75rem' : '0.8125rem' }}
          title={jobTitle}
        >
          {jobTitle}
        </div>
      ) : null}
    </div>
  );

  const avatar = (
    <div
      className={`dashboard-user-avatar rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${compact ? 'dashboard-user-avatar--sm' : ''}`}
      aria-hidden
    >
      {initials ? (
        <span className="dashboard-user-avatar__initials">{initials}</span>
      ) : (
        <i className="fa-solid fa-user" />
      )}
    </div>
  );

  if (compactAvatar) {
    return (
      <div
        ref={wrapRef}
        className="dashboard-user-profile dashboard-user-profile--avatar-only position-relative flex-shrink-0"
      >
        <button
          type="button"
          className="dashboard-user-avatar-btn"
          onClick={() => setPopoverOpen((v) => !v)}
          aria-expanded={popoverOpen}
          aria-haspopup="true"
          aria-label={`Account: ${displayName}`}
          title={displayName}
        >
          {avatar}
        </button>
        {popoverOpen && (
          <div className="dashboard-user-profile__popover" role="dialog">
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              <span className="fw-semibold dashboard-user-profile__name" title={displayName}>
                {displayName}
              </span>
              {hasRole(roles, 'support') && (
                <span className="badge rounded-pill bg-primary flex-shrink-0" style={{ fontSize: '11px' }}>
                  Support Rep
                </span>
              )}
            </div>
            {/* Linked accounts (ACOP-79).
                Read-only ON PURPOSE — switching lives in the left-nav picker, and having two
                controls that both change the active account invites the two disagreeing. This is
                the answer to "where do I see my other accounts?", not a second switcher, so the
                rows are plain text with a tick marking the active one.
                A single-account user sees exactly what they saw before: one plain line. */}
            {linkedAccounts.length > 1 ? (
              <div className="dashboard-user-profile__accounts" style={{ fontSize: '0.8125rem' }}>
                {linkedAccounts.map((a) => {
                  const isActive = a.id === accountCtx?.selectedAccountId;
                  return (
                    <div
                      key={a.id}
                      className={`d-flex align-items-start gap-2 ${isActive ? 'fw-semibold' : 'text-muted'}`}
                      title={isActive ? `${a.name} (active)` : a.name}
                    >
                      <i
                        className={`fa-solid fa-check flex-shrink-0 mt-1 ${isActive ? '' : 'invisible'}`}
                        aria-hidden
                        style={{ fontSize: '0.7rem' }}
                      />
                      <span className="text-break">
                        {a.name}
                        {isActive ? <span className="visually-hidden"> (active account)</span> : null}
                      </span>
                    </div>
                  );
                })}
                <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                  Switch accounts from the menu on the left.
                </div>
              </div>
            ) : company ? (
              <div className="text-muted dashboard-user-profile__job" style={{ fontSize: '0.8125rem' }}>
                {company}
              </div>
            ) : null}
            <hr className="my-2" />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={() => { setPopoverOpen(false); navigate('/profile'); }}
            >
              <i className="fa-solid fa-circle-user" aria-hidden />
              My profile
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2 mt-1"
              onClick={toggleDarkMode}
            >
              <i className={isDarkMode ? 'fa-solid fa-sun' : 'fa-solid fa-moon'} aria-hidden />
              {isDarkMode ? 'Light mode' : 'Dark mode'}
            </button>
            {onLogout && (
              <>
                <hr className="my-2" />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => { setPopoverOpen(false); onLogout(); }}
                  disabled={logoutBusy}
                >
                  <i className={logoutBusy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-right-from-bracket'} aria-hidden />
                  {logoutBusy ? 'Signing out…' : 'Sign out'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="d-flex align-items-center gap-2 dashboard-user-profile dashboard-user-profile--compact">
        <div className="d-none d-sm-block">{textBlock}</div>
        {avatar}
      </div>
    );
  }

  /* Text first (left), avatar on the right — explicit min-width so flex never collapses text to 0 */
  return (
    <div className="d-flex align-items-center gap-3 dashboard-user-profile">
      {textBlock}
      {avatar}
    </div>
  );
}
